/**
 * Inbound keyword detection for STOP/HELP family and localized variants.
 *
 * Carrier and CTIA guidance require STOP-family keywords to opt the recipient
 * out across the campaign and trigger a single confirmation. HELP must return
 * brand identity and support contact info. Non-standard phrases (e.g. "no mas",
 * "ya no", "remove me") aren't strictly required, but handling them protects
 * deliverability and avoids complaints.
 */

export type KeywordCategory = "STOP" | "HELP" | "START" | "UNKNOWN";

export interface KeywordMatch {
  category: KeywordCategory;
  matched: string;
  language: "en" | "es" | "unknown";
  standard: boolean; // true = carrier-required keyword; false = courtesy/extra
}

// CTIA / Twilio standard STOP-family keywords (must always be honored)
const STOP_STANDARD = [
  "STOP",
  "STOPALL",
  "UNSUBSCRIBE",
  "CANCEL",
  "END",
  "QUIT",
];

// Twilio Advanced Opt-Out additions and common courtesy keywords
const STOP_EXTENDED_EN = [
  "REVOKE",
  "OPTOUT",
  "OPT-OUT",
  "OPT OUT",
  "REMOVE",
  "REMOVE ME",
  "NO MORE",
  "DONT TEXT",
  "DON'T TEXT",
  "DO NOT TEXT",
];

const STOP_EXTENDED_ES = [
  "PARAR",
  "DETENER",
  "CANCELAR",
  "BAJA",
  "DARSE DE BAJA",
  "NO MAS",
  "NO MÁS",
  "YA NO",
  "ELIMINAR",
  "QUITAR",
  "QUITARME",
  "FUERA",
];

const HELP_STANDARD = ["HELP", "INFO"];
const HELP_EXTENDED_ES = ["AYUDA", "INFORMACION", "INFORMACIÓN"];

const START_STANDARD = ["START", "YES", "UNSTOP"];
const START_EXTENDED_ES = ["SI", "SÍ", "COMENZAR", "EMPEZAR", "ACEPTO"];

function normalize(input: string): string {
  return input.trim().toUpperCase().replace(/\s+/g, " ").replace(/[.,!?¡¿]/g, "");
}

export function detectKeyword(body: string): KeywordMatch {
  const n = normalize(body);

  if (STOP_STANDARD.includes(n)) {
    return { category: "STOP", matched: n, language: "en", standard: true };
  }
  if (STOP_EXTENDED_EN.includes(n)) {
    return { category: "STOP", matched: n, language: "en", standard: false };
  }
  if (STOP_EXTENDED_ES.includes(n)) {
    return { category: "STOP", matched: n, language: "es", standard: false };
  }
  if (HELP_STANDARD.includes(n)) {
    return { category: "HELP", matched: n, language: "en", standard: true };
  }
  if (HELP_EXTENDED_ES.includes(n)) {
    return { category: "HELP", matched: n, language: "es", standard: false };
  }
  if (START_STANDARD.includes(n)) {
    return { category: "START", matched: n, language: "en", standard: true };
  }
  if (START_EXTENDED_ES.includes(n)) {
    return { category: "START", matched: n, language: "es", standard: false };
  }

  // Whole-message contains-check for multi-word messages that start with keyword
  const first = n.split(" ")[0] ?? "";
  if (STOP_STANDARD.includes(first)) {
    return { category: "STOP", matched: first, language: "en", standard: true };
  }
  if (STOP_EXTENDED_ES.some((k) => n.startsWith(k))) {
    return { category: "STOP", matched: n, language: "es", standard: false };
  }

  return { category: "UNKNOWN", matched: n, language: "unknown", standard: false };
}
